import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { updatePlaylist } from '../../../services/api';
import { showToast } from '../../../utils/toast';

interface EditPlaylistModalProps {
  isOpen: boolean;
  onClose: () => void;
  playlistId: string;
  initialName: string;
  initialDescription: string;
  onSuccess: () => void;
}

export const EditPlaylistModal = memo(
  ({
    isOpen,
    onClose,
    playlistId,
    initialName,
    initialDescription,
    onSuccess,
  }: EditPlaylistModalProps) => {
    const { t } = useTranslation();
    const [name, setName] = useState(initialName);
    const [description, setDescription] = useState(initialDescription);
    const [isUpdating, setIsUpdating] = useState(false);
    const [updateError, setUpdateError] = useState(false);
    const nameInputRef = useRef<HTMLInputElement>(null);

    // Update state when modal opens with new playlist data
    useEffect(() => {
      if (isOpen) {
        setName(initialName);
        setDescription(initialDescription);
        setUpdateError(false);
      }
    }, [isOpen, initialName, initialDescription]);

    // Focus the name input when there's an update error
    useEffect(() => {
      if (updateError && nameInputRef.current) {
        nameInputRef.current.focus();
        nameInputRef.current.select();
      }
    }, [updateError]);

    const handleUpdate = useCallback(async () => {
      if (!name.trim()) {
        showToast.error(t('playlists.nameRequired'));
        return;
      }

      setIsUpdating(true);
      try {
        const result = await updatePlaylist(playlistId, name, description);
        if (result) {
          showToast.success(t('playlists.updatedSuccessfully'));
          onSuccess();
          onClose();
        }
      } catch (error: any) {
        console.error('Error updating playlist:', error);
        // Extract error message from the error
        const errorMessage = error?.message || 'Failed to update playlist';
        showToast.error(errorMessage);
        // Set error flag to trigger focus on input
        setUpdateError(true);
      } finally {
        setIsUpdating(false);
      }
    }, [playlistId, name, description, onSuccess, onClose]);

    if (!isOpen) return null;

    return (
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[9999] p-4"
        onClick={onClose}>
        <div
          className="bg-black/90 backdrop-blur-md border border-white/20 rounded-2xl shadow-2xl w-full max-w-md"
          onClick={(e) => e.stopPropagation()}>
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-white/10">
            <h2 className="text-xl font-semibold text-white">{t('playlists.editPlaylist')}</h2>
            <button
              onClick={onClose}
              className="text-white/60 hover:text-white transition-colors">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>

          {/* Content */}
          <div className="p-6 space-y-4">
            {/* Name Input */}
            <div>
              {/* <label className="block text-white text-sm text-left font-medium mb-2">
                Playlist Name
              </label> */}
              <input
                ref={nameInputRef}
                type="text"
                value={name}
                maxLength={200}
                onChange={(e) => {
                  setName(e.target.value);
                  // Clear error flag when user starts typing
                  setUpdateError(false);
                }}
                placeholder={t('playlists.enterName')}
                className="px-4 tc-mv-question-field  h-10 text-white rounded-full w-full mt-2"
              />
              {/* Character counter and error message - placeholder to avoid layout jump */}
              <div className="h-5 mt-2">
                {name.length === 200 ? (
                  <p className="text-xs text-red-400 font-medium">
                    {t('playlists.nameLimitReached')}
                  </p>
                ) : name.length >= 180 ? (
                  <span className="text-xs font-medium text-red-400">
                    {t('playlists.nameLimitApproaching', { count: name.length })}
                  </span>
                ) : null}
              </div>
            </div>

            {/* Description Input */}
            {/* <div>
              <label className="block text-white text-sm font-medium mb-2 text-left">
                Description
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter playlist description (optional)"
                className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:border-orange-500 transition-colors resize-none h-24"
              />
            </div> */}
          </div>

          {/* Footer */}
          <div className="flex gap-3 p-6 border-t border-white/10">
            <button
              onClick={() => {
                setUpdateError(false);
                onClose();
              }}
              disabled={isUpdating}
              className="flex-1 px-4 h-[44px] bg-[rgba(128,128,128,0.3)] hover:bg-[rgba(128,128,128,0.4)] border-t border-[rgba(255,255,255,0.5)] text-white rounded-[16px] font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center">
              {t('common.cancel')}
            </button>
            <button
              onClick={handleUpdate}
              disabled={isUpdating}
              className="flex-1 px-4 h-[44px] bg-white hover:bg-[#F2F2F2] disabled:opacity-50 text-black rounded-[16px] font-medium transition-colors disabled:cursor-not-allowed flex items-center justify-center">
              {isUpdating ? t('playlists.updating') : t('playlists.update')}
            </button>
          </div>
        </div>
      </div>
    );
  },
);

EditPlaylistModal.displayName = 'EditPlaylistModal';
